import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { Component, OnInit, OnDestroy, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';
import { EmpresaStateService } from '../../services/empresa-state.service';
import { PermissaoAcessoService } from '../../services/permissao-acesso.service';
import { AerosysGatewayService, AerosysEmpresa, AerosysVendaSeparacao } from '../../services/aerosys-gateway.service';
import { Subscription, interval } from 'rxjs';
export interface SeparacaoLinha {
  nunota: number;
  prioridade: string; // GPL_PRIORIDADE
  imediato: string;   // GPL_FATIMEDIATO
  nomeCliente: string;
  vendedor: string;
  status: string;     // GPL_STATUS
  sepCodusu: number;  // CODUSU de quem iniciou a separação (0 se não iniciada)
  sepNome: string;    // Nome do separador
  qtdSeparados: number;
  totalItens: number;
  prefixo?: string;   // Aeronave (AD_TCADAERO.PREFIXO)
  dataInicio?: string; // Data/hora início da separação
  dtneg?: string;     // Data de emissão da venda (DTNEG)
  numTag?: string;    // Nº da TAG informado ao iniciar a separação (GPL_SEPARACAO_ESTOQUE.NUMTAG)

  // ── Integração Aerosys (Sistema Legado) ──────────────────────────────────
  // Linhas oriundas do Aerosys são convertidas para este mesmo formato (ver
  // _mapLegadoParaLinha) para que a MESMA tabela sirva pras duas fontes —
  // usuários com a permissão EDTAERO veem só estas; os demais veem só os
  // pedidos do Sankhya (ver getter `linhas`). 'origem' e 'numTituloLegado'
  // existem só para exibir o selo "AERO" na coluna Nº Único.
  // Remover junto com o restante do bloco "Vendas do Sistema Legado" abaixo
  // quando o Aerosys for desativado.
  origem?: 'sankhya' | 'aerosys';
  numTituloLegado?: string;
  /** "Sininho" -- itens com pendência "Item no outro Galpão" já RESOLVIDA
   *  (alguém já buscou) mas ainda não marcados como separados de verdade --
   *  só existe pro fluxo Legado (ver ItemPendenciaResolvida no Aerosys). */
  itensPendenciaResolvida?: number;
}

@Component({
  selector: 'app-paineis-separacao',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './paineis-separacao.component.html',
  styleUrl: './paineis-separacao.component.css'
})
export class PaineisSeparacaoComponent implements OnInit, OnDestroy {
  @ViewChild('painelWrapper') painelWrapper!: ElementRef;

  // ── Atualização Automática ────────────────────────────────
  intervaloAtualizacao = 30; // Configuração (em segundos)
  tempoRestante = 30; // Contador visual na tela
  private tickSub?: Subscription;
  
  // ── Paginação ─────────────────────────────────────────────
  paginaAtual = 1;
  itensPorPagina = 17;
  readonly opcoesPorPagina = [17, 20, 25, 50, 100];

  // ── Estado ────────────────────────────────────────────────
  carregando = false;
  pedidos: SeparacaoLinha[] = [];
  erro = '';

  private sub?: Subscription;
  private _empresaSub?: Subscription;

  // ── Vendas do Sistema Legado (Aerosys) — fonte alternativa da tabela ─────
  // Liberado por uma permissão PRÓPRIA (EDTAERO no resource GPL_separacao —
  // diferente da VERAERO do app operacional, por pedido explícito: cada tela
  // libera por uma sigla separada).
  //
  // NÃO é um toggle manual: é uma troca automática de fonte de dados baseada
  // no usuário. Quem TEM a permissão EDTAERO vê SOMENTE as vendas do Aerosys
  // (Autorizado/Separação) nesta tabela; quem NÃO tem vê SOMENTE os pedidos
  // do Sankhya, como sempre foi. As duas filas nunca aparecem juntas nem
  // exigem clique — basta abrir o painel (ver ngOnInit → `legadoAberto` segue
  // `podeVerVendasLegado` direto).
  //
  // A única escolha manual que resta é QUAL empresa do Aerosys consultar
  // (várias empresas usam o sistema legado) — essa escolha é lembrada em
  // localStorage por navegador (ver _salvarEmpresaLegado / _restaurarEmpresaLegado).
  podeVerVendasLegado = false;
  legadoAberto = false;
  legadoEmpresas: AerosysEmpresa[] = [];
  legadoEmpresasCarregando = false;
  legadoEmpresasErro = '';
  legadoEmpresaSelecionada: AerosysEmpresa | null = null;
  legadoLista: AerosysVendaSeparacao[] = [];
  legadoCarregando = false;
  legadoErro = '';

  private readonly LEGADO_EMPRESAS_PERMITIDAS = [1, 3, 5, 7, 8, 9, 13, 15, 17];
  private readonly LEGADO_PREF_STORAGE_KEY = 'gpl_painel_separacao_legado_pref';

  /** Fonte de dados da tabela: usuário com permissão EDTAERO → SOMENTE vendas
   *  do Aerosys; sem a permissão → SOMENTE pedidos do Sankhya. Nunca as duas
   *  juntas, para não misturar as filas de quem trabalha com cada sistema. */
  get linhas(): SeparacaoLinha[] {
    if (this.legadoAberto) {
      return this.legadoLista.map(v => this._mapLegadoParaLinha(v));
    }
    return this.pedidos;
  }

  /** Loading da fonte ativa no momento (Sankhya ou Aerosys). */
  get carregandoLinhas(): boolean {
    return this.legadoAberto ? this.legadoCarregando : this.carregando;
  }

  /** Enquanto o usuário ainda está escolhendo a empresa do Aerosys (card acima
   *  da tabela), não faz sentido mostrar "nenhum pedido encontrado" embaixo. */
  get aguardandoEscolhaEmpresaLegado(): boolean {
    return this.legadoAberto && !this.legadoEmpresaSelecionada;
  }

  constructor(
    private gateway: SankhyaGatewayService,
    private aerosysGateway: AerosysGatewayService,
    private permissaoAcesso: PermissaoAcessoService,
    readonly empresaState: EmpresaStateService
  ) {}

  ngOnInit(): void {
    this.buscar();
    this.iniciarTimer();

    // Isolamento por empresa: painel gerencial (codusu=0, todos os separadores)
    // filtrado pela empresa logada — recarrega ao trocar a empresa selecionada
    // (mesmo padrão do EmpresaStateService usado no Pedido de Venda e nas telas
    // operacionais de Separação/Conferência/Logística).
    this._empresaSub = this.empresaState.empresaMudou.subscribe(() => {
      this.buscar();
    });

    this.permissaoAcesso.podeVerVendasLegadoAutorizadoPainel().then(pode => {
      this.podeVerVendasLegado = pode;
      // A fonte da tabela segue direto a permissão — quem tem EDTAERO já abre
      // vendo o Sistema Legado, sem precisar marcar nada. Só falta escolher
      // qual empresa do Aerosys consultar (carregarLegadoEmpresas cuida de
      // reaproveitar a empresa lembrada em localStorage, se houver).
      this.legadoAberto = pode;
      if (pode) {
        this.carregarLegadoEmpresas();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
    this._empresaSub?.unsubscribe();
    this.pararTimer();
  }

  /** Nome de exibição da empresa atualmente selecionada (para o badge no cabeçalho
   *  do painel — visível mesmo em tela cheia, onde o seletor da barra lateral some). */
  get nomeEmpresaAtual(): string {
    const emp = this.empresaState.opcoes.find(e => e.codEmp === this.empresaState.selecionada);
    return emp?.nomeFant || emp?.razaoAbrev || '';
  }

  // ── Controle do Timer ─────────────────────────────────────────────

  iniciarTimer(): void {
    this.pararTimer();
    
    if (this.intervaloAtualizacao && this.intervaloAtualizacao > 0) {
      this.tempoRestante = this.intervaloAtualizacao;
      // Tick de 1 em 1 segundo
      this.tickSub = interval(1000).subscribe(() => {
        this.tempoRestante--;
        if (this.tempoRestante <= 0) {
          this.atualizar();
          this.tempoRestante = this.intervaloAtualizacao;
        }
      });
    }
  }

  pararTimer(): void {
    if (this.tickSub) this.tickSub.unsubscribe();
  }

  onIntervaloChange(): void {
    // Se o usuário apagar o campo ou colocar < 1, paramos o auto-refresh.
    if (this.intervaloAtualizacao >= 1) {
      this.iniciarTimer();
    } else {
      this.pararTimer();
      this.tempoRestante = 0;
    }
  }

  // ── Tela Cheia (Fullscreen) ────────────────────────────────────────

  telaCheiaAtiva = false;

  alternarTelaCheia(): void {
    const el = this.painelWrapper.nativeElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen().then(() => {
        this.telaCheiaAtiva = true;
      }).catch((err: any) => {
        console.error('Erro ao tentar abrir tela cheia:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        this.telaCheiaAtiva = false;
      });
    }
  }

  // Monitora se o usuário apertou ESC para sair do fullscreen
  @HostListener('document:fullscreenchange', ['$event'])
  onFullscreenChange() {
    this.telaCheiaAtiva = !!document.fullscreenElement;
  }

  // ── Lógica de Listagem ────────────────────────────────────────────

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.linhas.length / this.itensPorPagina));
  }

  get pedidosPagina(): SeparacaoLinha[] {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    return this.linhas.slice(inicio, inicio + this.itensPorPagina);
  }

  get paginas(): number[] {
    const total = this.totalPaginas;
    const atual = this.paginaAtual;
    const delta = 2;
    const range: number[] = [];
    for (let i = Math.max(1, atual - delta); i <= Math.min(total, atual + delta); i++) {
      range.push(i);
    }
    return range;
  }

  irParaPagina(p: number): void {
    if (p < 1 || p > this.totalPaginas) return;
    this.paginaAtual = p;
  }

  onItensPorPaginaChange(): void {
    this.paginaAtual = 1;
  }

  /**
   * Ponto único de "atualizar a lista" -- usado pelo timer automático e pelo
   * botão manual "Atualizar". Refaz a busca da fonte REALMENTE exibida no
   * momento (ver getter `linhas`): Aerosys/Legado quando `legadoAberto`,
   * Sankhya (`buscar()`) caso contrário.
   *
   * Bug corrigido aqui: antes, tanto o timer quanto o botão chamavam
   * `buscar()` direto, que só re-busca `pedidos` (Sankhya) -- pra quem tem a
   * permissão EDTAERO (vê `legadoLista`, não `pedidos`), a lista na tela
   * nunca mudava, mesmo com o contador do timer reiniciando normalmente.
   */
  atualizar(): void {
    this.tempoRestante = this.intervaloAtualizacao;
    if (this.legadoAberto) {
      // Sem empresa do Aerosys escolhida ainda não há o que atualizar --
      // ver getter aguardandoEscolhaEmpresaLegado, que já cobre esse estado.
      if (this.legadoEmpresaSelecionada) {
        this.buscarVendasLegado();
      }
    } else {
      this.buscar();
    }
  }

  buscar(): void {
    this.carregando = true;
    this.erro = '';
    
    // Reinicia o contador caso o usuário clique manualmente
    this.tempoRestante = this.intervaloAtualizacao;

    // codusu=0 → visão gerencial (todos os separadores), filtrado pela empresa
    // logada em vez de trazer todas as empresas misturadas.
    const codEmp = Number(this.empresaState.selecionada ?? 1);
    this.sub = this.gateway.buscarPedidosSeparacao(0, codEmp).subscribe({
      next: (res: any) => {
        this.carregando = false;
        if (res && res.sucesso && Array.isArray(res.pedidos)) {
          this.pedidos = res.pedidos;
        } else {
          this.erro = res?.erro || 'Erro desconhecido ao carregar pedidos de separação.';
        }
        this.paginaAtual = 1;
      },
      error: (err) => {
        this.carregando = false;
        this.erro = 'Falha de comunicação com o servidor. Tente novamente.';
        console.error(err);
      }
    });
  }

  statusClass(status: string): string {
    const s = status?.toLowerCase() ?? '';
    if (s === 'autorizado' || s === 'autorizados') return 'status-autorizado';
    if (s === 'separação' || s === 'separacao')    return 'status-separacao';
    return 'status-outro';
  }

  /** AOG (vermelho), Urgente (laranja), Alta (vermelho claro) e Normal (verde) --
   *  pedido do cliente. Usa "includes" (não igualdade estrita) porque o valor
   *  cru às vezes vem com prefixo numérico do Sankhya, ex.: "1-AOG", "3-URGENTE". */
  prioridadeClass(prioridade: string | null | undefined): string {
    const p = (prioridade ?? '').trim().toLowerCase();
    if (p.includes('aog')) return 'badge-aog';
    if (p.includes('urgente')) return 'badge-urgente';
    if (p.includes('alta')) return 'badge-alta';
    return 'badge-normal';
  }

  // ── Vendas do Sistema Legado (Aerosys) — fonte alternativa da tabela ─────
  // Bloco isolado de propósito (ver comentário no topo do arquivo) — todo ele
  // pode ser removido junto com a permissão EDTAERO quando o Aerosys for
  // desativado, sem afetar o restante do Painel de Separação.

  /** Converte uma venda "rica" do Aerosys (VendaSeparacaoDTO) para o mesmo
   *  formato de linha usado pelos pedidos do Sankhya, para que a tabela e a
   *  paginação tratem as duas origens de forma uniforme. */
  private _mapLegadoParaLinha(v: AerosysVendaSeparacao): SeparacaoLinha {
    const totalItens = v.totalItens ?? 0;
    const qtdSeparados = v.itensSeparados ?? 0;
    const imediatoTruthy = ['S', 'SIM', 'TRUE', '1'].includes((v.fatImediato ?? '').toUpperCase());
    return {
      // Nº "sintético" só para uso interno (ordenação/trackBy) — exibido via numTituloLegado.
      nunota: -Math.abs(v.id),
      prioridade: v.prioridade || 'Normal',
      imediato: imediatoTruthy ? 'Sim' : 'Não',
      nomeCliente: v.clienteNome || '—',
      vendedor: v.vendedorNome || '—',
      status: v.status || '—',
      sepCodusu: 0,
      sepNome: v.separadorNome || '',
      qtdSeparados,
      totalItens,
      prefixo: v.prefixoAeronave || undefined, // AERONAVE.AER_MATRICULA via VENDA.AER_ID (ver VendaSeparacaoDTO).
      // O Sankhya já entrega dtneg/dataInicio pré-formatados (TO_CHAR ...,
      // 'DD/MM/YYYY [HH24:MI]') — normalizamos as datas do Aerosys para o
      // mesmo formato aqui, já que as duas origens agora dividem a mesma coluna.
      dataInicio: this._formatarDataBr(v.dataInicioSep, true),
      dtneg: this._formatarDataBr(v.dataEmissao, false),
      origem: 'aerosys',
      numTituloLegado: v.numTitulo || String(v.id),
    };
  }

  /** Normaliza uma data (ISO ou epoch, dependendo de como o Jackson do Aerosys
   *  serializa) para o padrão DD/MM/YYYY (ou DD/MM/YYYY HH:mm com comHora=true)
   *  usado pelas colunas do Painel. Retorna undefined se a data vier vazia/inválida. */
  private _formatarDataBr(valor: string | null | undefined, comHora: boolean): string | undefined {
    if (!valor) return undefined;
    const d = new Date(valor);
    if (isNaN(d.getTime())) return undefined;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    if (!comHora) return `${dd}/${mm}/${yyyy}`;
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  carregarLegadoEmpresas(): void {
    this.legadoEmpresasCarregando = true;
    this.legadoEmpresasErro = '';
    this.aerosysGateway.listarEmpresas().subscribe({
      next: (lista) => {
        const todas = Array.isArray(lista) ? lista : [];
        this.legadoEmpresas = todas.filter(e => this.LEGADO_EMPRESAS_PERMITIDAS.includes(Number(e.id)));
        this.legadoEmpresasCarregando = false;

        // Reabre na empresa lembrada (localStorage); se não houver nenhuma
        // lembrada ainda (1ª vez do usuário nesta tela), cai pra empresa 1 por
        // padrão -- pedido do cliente pra tela já "nascer" com dados, sem
        // exigir escolha manual antes de ver algo. O usuário troca a qualquer
        // momento pelo seletor no cabeçalho (ver onLegadoEmpresaSelectChange).
        const empresaLembrada = this._restaurarEmpresaLegado();
        let alvo = empresaLembrada != null
          ? this.legadoEmpresas.find(e => Number(e.id) === empresaLembrada)
          : undefined;
        if (!alvo) {
          alvo = this.legadoEmpresas.find(e => Number(e.id) === 1);
        }
        if (alvo) this.selecionarLegadoEmpresa(alvo, false);
      },
      error: (err: any) => {
        this.legadoEmpresasErro = err?.error?.detail ?? err?.message ?? 'Erro ao carregar empresas do Aerosys.';
        this.legadoEmpresasCarregando = false;
      }
    });
  }

  selecionarLegadoEmpresa(empresa: AerosysEmpresa, persistir = true): void {
    this.legadoEmpresaSelecionada = empresa;
    if (persistir) this._salvarEmpresaLegado(empresa.id);
    this.buscarVendasLegado();
  }

  /** Handler do <select> de empresa no cabeçalho (ver template) -- só troca de fato se for uma empresa diferente da atual. */
  onLegadoEmpresaSelectChange(id: number | string | null): void {
    if (id == null) return;
    const empresa = this.legadoEmpresas.find(e => Number(e.id) === Number(id));
    if (empresa && Number(empresa.id) !== Number(this.legadoEmpresaSelecionada?.id)) {
      this.selecionarLegadoEmpresa(empresa);
    }
  }

  buscarVendasLegado(): void {
    if (!this.legadoEmpresaSelecionada) return;
    this.legadoCarregando = true;
    this.legadoErro = '';

    this.aerosysGateway.listarSeparacaoPainel(this.legadoEmpresaSelecionada.id).subscribe({
      next: (lista) => {
        this.legadoLista = Array.isArray(lista) ? lista : [];
        this.legadoCarregando = false;
      },
      error: (err: any) => {
        this.legadoErro = err?.error?.detail ?? err?.message ?? 'Erro ao buscar Vendas do Sistema Legado.';
        this.legadoCarregando = false;
      }
    });
  }

  // ── Preferência persistida (localStorage) ────────────────────────
  // A fonte (Sankhya x Aerosys) já é automática pela permissão EDTAERO — só
  // resta lembrar QUAL empresa do Aerosys o usuário escolheu, para não pedir
  // de novo a cada sessão.

  private _salvarEmpresaLegado(empresaId: number | null): void {
    try {
      localStorage.setItem(this.LEGADO_PREF_STORAGE_KEY, JSON.stringify({ empresaId }));
    } catch {
      // localStorage indisponivel (modo privado etc.) - sem persistencia, sem quebrar a tela.
    }
  }

  private _restaurarEmpresaLegado(): number | null {
    try {
      const raw = localStorage.getItem(this.LEGADO_PREF_STORAGE_KEY);
      if (!raw) return null;
      const pref = JSON.parse(raw);
      return typeof pref?.empresaId === 'number' ? pref.empresaId : null;
    } catch {
      return null;
    }
  }

}
